import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Star, Loader, ArrowLeft } from 'lucide-react';
import { toast } from '../hooks/use-toast';
import StarRating from '../components/StarRating';
import ReviewModal from '../components/ReviewModal';

const Reviews = () => {
  const navigate = useNavigate();
  const [pendingRides, setPendingRides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRide, setSelectedRide] = useState(null);
  const [userRole, setUserRole] = useState('rider');
  const [showReviewModal, setShowReviewModal] = useState(false);

  useEffect(() => {
    fetchPendingReviews();
  }, []);

  const fetchPendingReviews = async () => {
    try {
      const api = (await import('../utils/api')).default;
      
      // Get current user to determine role
      const userResponse = await api.get('/auth/me');
      const currentUser = userResponse.data.user;
      
      // Fetch pending reviews
      const response = await api.get('/reviews/pending');
      const rides = response.data.pendingRides || [];
      
      // Determine user role for each ride
      const enrichedRides = rides.map(ride => ({
        ...ride,
        userRole: ride.userId === currentUser.id ? 'rider' : 'driver'
      }));
      
      setPendingRides(enrichedRides);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching pending reviews:', error);
      toast({
        title: 'Error',
        description: 'Failed to load pending reviews',
        variant: 'destructive',
      });
      setLoading(false);
    }
  };

  const handleReviewClick = (ride) => {
    setSelectedRide(ride);
    setUserRole(ride.userRole);
    setShowReviewModal(true);
  };

  const handleReviewSubmitted = () => {
    // Refresh pending reviews
    fetchPendingReviews();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader className="h-12 w-12 animate-spin text-pink-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center gap-4">
            <Button
              onClick={() => navigate(-1)}
              variant="ghost"
              className="p-2"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Pending Reviews</h1>
              <p className="text-gray-600 mt-1">
                Rate your recent rides and deliveries
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {pendingRides.length === 0 ? (
          <Card className="p-8 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
              <Star className="h-8 w-8 text-green-600 fill-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">All Caught Up!</h2>
            <p className="text-gray-600">
              You don't have any rides waiting to be reviewed.
            </p>
          </Card>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center justify-between mb-4">
              <p className="text-sm text-gray-600">
                {pendingRides.length} {pendingRides.length === 1 ? 'ride' : 'rides'} waiting for review
              </p>
            </div>

            {pendingRides.map((ride) => (
              <Card key={ride.id} className="p-6 hover:shadow-lg transition-shadow">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-3">
                      <Badge className="bg-pink-100 text-pink-800">
                        {ride.type === 'delivery' ? 'Delivery' : 'Ride'}
                      </Badge>
                      {ride.userRole === 'driver' && (
                        <Badge className="bg-blue-100 text-blue-800">
                          As Driver
                        </Badge>
                      )}
                    </div>

                    <div className="space-y-2 mb-4">
                      <div>
                        <p className="text-sm text-gray-600">Pickup</p>
                        <p className="font-medium text-gray-900">{ride.pickup}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Dropoff</p>
                        <p className="font-medium text-gray-900">{ride.dropoff}</p>
                      </div>
                    </div>

                    <div className="flex items-center gap-4 text-sm text-gray-600">
                      <span>K{ride.price?.toFixed(2)}</span>
                      <span>•</span>
                      <span>{new Date(ride.completedAt).toLocaleDateString()}</span>
                    </div>
                  </div>

                  <Button
                    onClick={() => handleReviewClick(ride)}
                    className="bg-pink-600 hover:bg-pink-700 flex items-center gap-2"
                  >
                    <Star className="h-4 w-4" />
                    Rate {ride.userRole === 'rider' ? 'Driver' : 'Passenger'}
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Review Modal */}
      <ReviewModal
        isOpen={showReviewModal}
        onClose={() => setShowReviewModal(false)}
        ride={selectedRide}
        userRole={userRole}
        onSubmitSuccess={handleReviewSubmitted}
      />
    </div>
  );
};

export default Reviews;
